import { Module } from 'vuex';
import { IResourceModel } from '@/shared/model/briefing-widget.model';
import { BriefingResourceRequest, LoadingDataReportCard, ToastReportData } from '@/shared/model/briefing-resource.model';
import { convertToBriefingResourceReq } from '@/util/ds-util';
import { CHAT_SETTING } from '@/shared/constants/ds-constants';

export interface AppStorable {
  isLoginPage: boolean;
  isShowAddReference: boolean;
  isShowDetailReport: boolean;
  isShowDetailTopic: boolean;
  isShowDetailArticle: boolean;
  isShowCreateBriefing: boolean;
  isShowEditReportRef: boolean;
  isID: string;
  isOpenFirstStepDeleteAccountModal: boolean;
  isOpenSecondStepDeleteAccountModal: boolean;
  isOpenResetServiceModal: boolean;
  currentUserImageUri: string;
  isCallLoadBriefing: boolean;
  colorTheme: string;
  isSelectFirstBriefingPage: boolean;
  isShowUpdateBriefing: boolean;
  isShowSuccessUpdateBriefing: boolean;
  notifyResourceItem: Array<BriefingResourceRequest>;
  isShowAlertPinBriefing: boolean;
  isShowDeleteBriefing: boolean;
  isDeleteSingleBriefing: boolean;
  currentDeleteBriefingName: string;
  numberOfDeleteBriefings: number;
  currentDeleteBriefingId: string;
  currentBriefingSummary: string;
  isLoadingDeleteBriefing: boolean;
  isShowShareReport: boolean;
  isShowShareTopic: boolean;
  isShowBackdrop: boolean;
  isDisabledLink: boolean;
  isShowReportSettingReportType: boolean;
  isShowReportWarningCloseAfterEdit: boolean;
  toastReportData: Array<ToastReportData>;
  bpIdForReportWithBriefing: string;
  dataReportNotify: any;
  goToTopClicked: boolean;
  reShowToastReport: boolean;
  isShowPopupSubmitReport: boolean;
  isShowPopupSubmitReportSuccess: boolean;
  toggleResource: boolean;
  isOpenPopupEditReferenceForPage: boolean;
  isShowPopupRequiredLogin: boolean;
  isShowPopupLogin: boolean;
  isShowPopupRegister: boolean;
  errorElementId: string[];
  isLoadingUpdateLanguage: boolean;
  isShowPopupRequiredPaidAccount: boolean;
  isShowPopupRequiredOnlyUltra: boolean;
  isShowFeatureLimit: boolean;
  isShowPopupCancelPayment: boolean;
  chatSetting: any;
  isCanShowCreateAgent: boolean;
  checkToMoveToAskSub: boolean;
  isShowAlertDeepResearch: boolean;
  dataPodcastNotify: any;
}

export const appStore: Module<AppStorable, any> = {
  namespaced: true,
  state: {
    isLoginPage: true,
    isShowAddReference: false,
    isShowDetailReport: false,
    isShowDetailTopic: false,
    isShowDetailArticle: false,
    isShowCreateBriefing: false,
    isID: null,
    isOpenFirstStepDeleteAccountModal: false,
    isOpenSecondStepDeleteAccountModal: false,
    isOpenResetServiceModal: false,
    currentUserImageUri: 'content/images/avatar.png',
    isCallLoadBriefing: false,
    colorTheme: 'light',
    isSelectFirstBriefingPage: false,
    isShowUpdateBriefing: false,
    isShowSuccessUpdateBriefing: false,
    isShowEditReportRef: false,
    notifyResourceItem: [],
    isShowAlertPinBriefing: false,
    isShowDeleteBriefing: false,
    isDeleteSingleBriefing: false,
    currentDeleteBriefingName: '',
    numberOfDeleteBriefings: 0,
    currentDeleteBriefingId: '',
    currentBriefingSummary: '',
    isLoadingDeleteBriefing: false,
    isShowShareReport: false,
    isShowShareTopic: false,
    isShowBackdrop: false,
    isDisabledLink: false,
    isShowReportSettingReportType: false,
    isShowReportWarningCloseAfterEdit: false,
    toastReportData: [],
    bpIdForReportWithBriefing: '',
    dataReportNotify: null,
    goToTopClicked: false,
    reShowToastReport: false,
    isShowPopupSubmitReport: false,
    isShowPopupSubmitReportSuccess: false,
    toggleResource: false,
    isOpenPopupEditReferenceForPage: false,
    isShowPopupRequiredLogin: false,
    isShowPopupLogin: false,
    isShowPopupRegister: false,
    errorElementId: [''],
    isLoadingUpdateLanguage: false,
    isShowPopupRequiredPaidAccount: false,
    isShowPopupRequiredOnlyUltra: false,
    isShowFeatureLimit: false,
    isShowPopupCancelPayment: false,
    chatSetting: CHAT_SETTING,
    isCanShowCreateAgent: true,
    checkToMoveToAskSub: false,
    isShowAlertDeepResearch: true,
    dataPodcastNotify: null,
  },
  getters: {
    isLoginPage: state => state.isLoginPage,
    isShowAddReference: state => state.isShowAddReference,
    isShowDetailReport: state => state.isShowDetailReport,
    isShowDetailTopic: state => state.isShowDetailTopic,
    isShowDetailArticle: state => state.isShowDetailArticle,
    isShowCreateBriefing: state => state.isShowCreateBriefing,
    getId: state => state.isID,
    getIsOpenFirstStepDeleteAccountModal: state => state.isOpenFirstStepDeleteAccountModal,
    getIsOpenSecondStepDeleteAccountModal: state => state.isOpenSecondStepDeleteAccountModal,
    getIsOpenResetServiceModal: state => state.isOpenResetServiceModal,
    getCurrentUserImageUri: state => state.currentUserImageUri,
    getIsCallLoadBriefing: state => state.isCallLoadBriefing,
    getColorTheme: state => state.colorTheme,
    getIsSelectFirstBriefingPage: state => state.isSelectFirstBriefingPage,
    getIsShowUpdateBriefing: state => state.isShowUpdateBriefing,
    getIsShowSuccessUpdateBriefing: state => state.isShowSuccessUpdateBriefing,
    getIsShowEditReportRef: state => state.isShowEditReportRef,
    getNotifyResourceItem: state => state.notifyResourceItem,
    getIsShowAlertPinBriefing: state => state.isShowAlertPinBriefing,
    getIsShowDeleteBriefing: state => state.isShowDeleteBriefing,
    getIsDeleteSingleBriefing: state => state.isDeleteSingleBriefing,
    getCurrentDeleteBriefingName: state => state.currentDeleteBriefingName,
    getNumberOfDeleteBriefings: state => state.numberOfDeleteBriefings,
    getCurrentDeleteBriefingId: state => state.currentDeleteBriefingId,
    getCurrentBriefingSummary: state => state.currentBriefingSummary,
    getIsLoadingDeleteBriefing: state => state.isLoadingDeleteBriefing,
    getIsShowShareReport: state => state.isShowShareReport,
    getIsShowShareTopic: state => state.isShowShareTopic,
    getIsShowBackdrop: state => state.isShowBackdrop,
    isDisabledLink: state => state.isDisabledLink,
    getIsShowReportSettingReportType: state => state.isShowReportSettingReportType,
    getIsShowReportWarningCloseAfterEdit: state => state.isShowReportWarningCloseAfterEdit,
    getToastReportData: state => state.toastReportData,
    getBpIdForReportWithBriefing: state => state.bpIdForReportWithBriefing,
    getDataReportNotify: state => state.dataReportNotify,
    getGoToTopClicked: state => state.goToTopClicked,
    getReShowToastReport: state => state.reShowToastReport,
    getIsShowPopupSubmitReport: state => state.isShowPopupSubmitReport,
    getIsShowPopupSubmitReportSuccess: state => state.isShowPopupSubmitReportSuccess,
    getToggleResource: state => state.toggleResource,
    getIsOpenPopupEditReferenceForPage: state => state.isOpenPopupEditReferenceForPage,
    getIsShowPopupRequiredLogin: state => state.isShowPopupRequiredLogin,
    getIsShowPopupLogin: state => state.isShowPopupLogin,
    getIsShowPopupRegister: state => state.isShowPopupRegister,
    getErrorElementId: state => state.errorElementId,
    getIsLoadingUpdateLanguage: state => state.isLoadingUpdateLanguage,
    getIsShowPopupRequiredPaidAccount: state => state.isShowPopupRequiredPaidAccount,
    getIsShowPopupRequiredOnlyUltra: state => state.isShowPopupRequiredOnlyUltra,
    getIsShowFeatureLimit: state => state.isShowFeatureLimit,
    getIsShowPopupCancelPayment: state => state.isShowPopupCancelPayment,
    getChatSetting: state => state.chatSetting,
    getIsCanShowCreateAgent: state => state.isCanShowCreateAgent,
    getCheckToMoveToAskSub: state => state.checkToMoveToAskSub,
    getIsShowAlertDeepResearch: state => state.isShowAlertDeepResearch,
    getDataPodcastNotify: state => state.dataPodcastNotify,
  },
  mutations: {
    setPageLogin(state, isLoginPage: boolean) {
      state.isLoginPage = isLoginPage;
    },
    setShowAddReference(state, isShowAddReference: boolean) {
      state.isShowAddReference = isShowAddReference;
    },
    setShowDetailReport(state, isShowDetail: boolean) {
      state.isShowDetailReport = isShowDetail;
    },
    setShowDetailTopic(state, isShowDetail: boolean) {
      state.isShowDetailTopic = isShowDetail;
    },
    setShowCreateBriefing(state, isShowCreateBriefing: boolean) {
      state.isShowCreateBriefing = isShowCreateBriefing;
    },
    setShowEditReportRef(state, isShowEditReportRef: boolean) {
      state.isShowEditReportRef = isShowEditReportRef;
    },
    setShowDetailArticles(state, isShowDetailArticle: boolean) {
      state.isShowDetailArticle = isShowDetailArticle;
    },
    setId(state, id: string) {
      state.isID = id;
    },
    setIsOpenFirstStepDeleteAccountModal(state, isOpenFirstStepDeleteAccountModal) {
      state.isOpenFirstStepDeleteAccountModal = isOpenFirstStepDeleteAccountModal;
    },
    setIsOpenSecondStepDeleteAccountModal(state, isOpenSecondStepDeleteAccountModal) {
      state.isOpenSecondStepDeleteAccountModal = isOpenSecondStepDeleteAccountModal;
    },
    setIsOpenResetServiceModal(state, isOpenResetServiceModal) {
      state.isOpenResetServiceModal = isOpenResetServiceModal;
    },
    setCurrentUserImageUri(state, currentUserImageUri) {
      state.currentUserImageUri = currentUserImageUri;
    },
    setIsCallLoadBriefing(state, isCallLoadBriefing) {
      state.isCallLoadBriefing = isCallLoadBriefing;
    },
    setColorTheme(state, colorThemeInput) {
      state.colorTheme = colorThemeInput;
    },
    setIsSelectFirstBriefingPage(state, isSelectFirstBriefingPage) {
      state.isSelectFirstBriefingPage = isSelectFirstBriefingPage;
    },
    setIsShowUpdateBriefing(state, isShowUpdateBriefing) {
      state.isShowUpdateBriefing = isShowUpdateBriefing;
    },
    setIsShowSuccessUpdateBriefing(state, isShowSuccessUpdateBriefing) {
      state.isShowSuccessUpdateBriefing = isShowSuccessUpdateBriefing;
    },
    setNotifyResourceItem(state, notifyResourceItem) {
      state.notifyResourceItem = notifyResourceItem;
    },
    setGoToTopClicked(state, value) {
      state.goToTopClicked = value;
    },
    addToNotifyResourceItem(state, notifyResourceItem: IResourceModel) {
      const briefingResourceReqItem = convertToBriefingResourceReq(
        notifyResourceItem,
        notifyResourceItem?.userProfile?.userKey,
        '',
        undefined,
        [],
        undefined,
        notifyResourceItem?.userProfile?.lastName,
        'Notify-' + Date.now().toString(),
        true
      );
      if (!briefingResourceReqItem?.writer?.[0] && notifyResourceItem?.channel) {
        briefingResourceReqItem.writer[0] = notifyResourceItem.channel;
      }
      briefingResourceReqItem.published_at = notifyResourceItem.originParentDocDetail?.published_at || '';
      briefingResourceReqItem.docType = 2;
      if (notifyResourceItem?.originParentDocDetail?.image_uri_thumbnail) {
        briefingResourceReqItem.image_uri_thumbnail = notifyResourceItem.originParentDocDetail.image_uri_thumbnail;
      }
      delete briefingResourceReqItem['isNew'];
      // @ts-ignore
      briefingResourceReqItem.mediaType = notifyResourceItem.originParentDocDetail.media_type;
      // @ts-ignore
      briefingResourceReqItem.search_type = notifyResourceItem.originParentDocDetail.search_type;
      briefingResourceReqItem.isBriefingDataNotify =
        notifyResourceItem.isBriefingDataNotify !== undefined && notifyResourceItem.isBriefingDataNotify === true;
      state.notifyResourceItem.unshift(briefingResourceReqItem);
    },
    setIsShowAlertPinBriefing(state, newVal: boolean) {
      state.isShowAlertPinBriefing = newVal;
    },
    setIsShowDeleteBriefing(state, isShowDeleteBriefing: boolean) {
      state.isShowDeleteBriefing = isShowDeleteBriefing;
    },
    setIsDeleteSingleBriefing(state, isDeleteSingleBriefing: boolean) {
      state.isDeleteSingleBriefing = isDeleteSingleBriefing;
    },
    setCurrentDeleteBriefingName(state, currentDeleteBriefingName: string) {
      state.currentDeleteBriefingName = currentDeleteBriefingName;
    },
    setNumberOfDeleteBriefings(state, numberOfDeleteBriefings: number) {
      state.numberOfDeleteBriefings = numberOfDeleteBriefings;
    },
    setCurrentDeleteBriefingId(state, currentDeleteBriefingId: string) {
      state.currentDeleteBriefingId = currentDeleteBriefingId;
    },
    setCurrentBriefingSummary(state, currentBriefingSummary: string) {
      state.currentBriefingSummary = currentBriefingSummary;
    },
    setIsLoadingDeleteBriefing(state, isLoadingDeleteBriefing: boolean) {
      state.isLoadingDeleteBriefing = isLoadingDeleteBriefing;
    },
    setIsShowShareReport(state, isShowShareReport: boolean) {
      state.isShowShareReport = isShowShareReport;
    },
    setIsShowShareTopic(state, isShowShareTopic: boolean) {
      state.isShowShareTopic = isShowShareTopic;
    },
    setIsShowBackdrop(state, isShowBackdrop: boolean) {
      state.isShowBackdrop = isShowBackdrop;
    },
    setDisabledLink(state, isDisabledLink: boolean) {
      state.isDisabledLink = isDisabledLink;
    },
    setIsShowReportSettingReportType(state, isShowReportSettingReportType: boolean) {
      state.isShowReportSettingReportType = isShowReportSettingReportType;
    },
    setIsShowReportWarningCloseAfterEdit(state, isShowReportWarningCloseAfterEdit: boolean) {
      state.isShowReportWarningCloseAfterEdit = isShowReportWarningCloseAfterEdit;
    },
    setToastReportData(state, toastReportData: Array<ToastReportData>) {
      state.toastReportData = toastReportData;
    },
    setBpIdForReportWithBriefing(state, bpIdForReportWithBriefing: string) {
      state.bpIdForReportWithBriefing = bpIdForReportWithBriefing;
    },
    setDataReportNotify(state, dataReportNotify) {
      state.dataReportNotify = dataReportNotify;
    },
    setReShowToastReport(state, reShowToastReport: boolean) {
      state.reShowToastReport = reShowToastReport;
    },
    setIsShowPopupSubmitReport(state, isShowPopupSubmitReport) {
      state.isShowPopupSubmitReport = isShowPopupSubmitReport;
    },
    setIsShowPopupSubmitReportSuccess(state, isShowPopupSubmitReportSuccess) {
      state.isShowPopupSubmitReportSuccess = isShowPopupSubmitReportSuccess;
    },
    setToggleResource(state, toggleResource) {
      state.toggleResource = toggleResource;
    },
    setIsOpenPopupEditReferenceForPage(state, isOpenPopupEditReferenceForPage) {
      state.isOpenPopupEditReferenceForPage = isOpenPopupEditReferenceForPage;
    },
    setIsShowPopupRequiredLogin(state, isShowPopupRequiredLogin: boolean) {
      state.isShowPopupRequiredLogin = isShowPopupRequiredLogin;
    },
    setIsShowPopupLogin(state, isShowPopupLogin: boolean) {
      state.isShowPopupLogin = isShowPopupLogin;
    },
    setIsShowPopupRegister(state, isShowPopupRegister: boolean) {
      state.isShowPopupRegister = isShowPopupRegister;
    },
    setErrorElementId(state, errorElementId: string[]) {
      state.errorElementId = errorElementId;
    },
    setIsLoadingUpdateLanguage(state, isLoadingUpdateLanguage: boolean) {
      state.isLoadingUpdateLanguage = isLoadingUpdateLanguage;
    },
    setIsShowPopupRequiredPaidAccount(state, isShowPopupRequiredPaidAccount: boolean) {
      state.isShowPopupRequiredPaidAccount = isShowPopupRequiredPaidAccount;
    },
    setIsShowPopupRequiredOnlyUltra(state, isShowPopupRequiredOnlyUltra: boolean) {
      state.isShowPopupRequiredOnlyUltra = isShowPopupRequiredOnlyUltra;
    },
    setIsShowFeatureLimit(state, isShowFeatureLimit: boolean) {
      state.isShowFeatureLimit = isShowFeatureLimit;
    },
    setIsShowPopupCancelPayment(state, isShowPopupCancelPayment: boolean) {
      state.isShowPopupCancelPayment = isShowPopupCancelPayment;
    },
    setChatSetting(state, chatSetting) {
      state.chatSetting = chatSetting;
    },
    setIsCanShowCreateAgent(state, isCanShowCreateAgent) {
      state.isCanShowCreateAgent = isCanShowCreateAgent;
    },
    setCheckToMoveToAskSub(state, checkToMoveToAskSub) {
      state.checkToMoveToAskSub = checkToMoveToAskSub;
    },
    setIsShowAlertDeepResearch(state, isShowAlertDeepResearch) {
      state.isShowAlertDeepResearch = isShowAlertDeepResearch;
    },
    setDataPodcastNotify(state, dataPodcastNotify) {
      state.dataPodcastNotify = dataPodcastNotify;
    },
  },
  actions: {
    changePage(context, payload: { isLoginPage: boolean }) {
      context.commit('setPageLogin', payload.isLoginPage);
    },
  },
};
